import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Zap, Clock, Flame, Phone, Mail, MapPin, Globe, ArrowRight, Sparkles, Shield, Award, CheckCircle, Users, Ruler, Palette, Briefcase } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Hero images based on industry
const HERO_IMAGES = {
  memorials: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=1920&q=80',
  worktops: 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=1920&q=80',
  default: 'https://images.unsplash.com/photo-1566041510394-cf7c8fe21800?w=1920&q=80'
};

function CompanyLandingPage() {
  const { companySlug } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [settings, setSettings] = useState(null);
  const [showTradeAccountDialog, setShowTradeAccountDialog] = useState(false);
  const [tradeAccountForm, setTradeAccountForm] = useState({
    business_name: '',
    contact_name: '',
    email: '',
    phone: '',
    address: '',
    business_type: '',
    how_heard: ''
  });
  const [submittingTradeAccount, setSubmittingTradeAccount] = useState(false);
  const [tradeAccountSubmitted, setTradeAccountSubmitted] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, [companySlug]);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/public/company/${companySlug}/landing-settings`);
      setSettings(response.data);
    } catch (error) {
      setError('Company not found');
    } finally {
      setLoading(false);
    }
  };

  const handleTradeAccountSubmit = async (e) => {
    e.preventDefault();
    if (!tradeAccountForm.business_name || !tradeAccountForm.contact_name || !tradeAccountForm.email || !tradeAccountForm.phone) {
      return;
    }
    
    setSubmittingTradeAccount(true);
    try {
      await axios.post(`${API}/public/company/${companySlug}/trade-account-request`, tradeAccountForm);
      setTradeAccountSubmitted(true);
    } catch (error) {
      console.error('Failed to submit trade account request:', error);
    } finally {
      setSubmittingTradeAccount(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-stone-200 border-t-stone-800 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-stone-500 font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  if (error || !settings) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center p-6">
        <Card className="max-w-md w-full shadow-xl border-0">
          <CardContent className="p-10 text-center">
            <div className="w-20 h-20 bg-stone-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <MapPin className="w-10 h-10 text-stone-400" />
            </div>
            <h1 className="text-2xl font-serif font-medium text-stone-900 mb-3">Company Not Found</h1>
            <p className="text-stone-500 mb-8">The company you're looking for doesn't exist or has been removed.</p>
            <Button 
              onClick={() => navigate('/')}
              className="bg-stone-900 hover:bg-stone-800 text-white rounded-full px-8 py-3"
            >
              Go Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const heroImage = settings.industry_type === 'worktops' ? HERO_IMAGES.worktops : 
                    settings.industry_type === 'memorials' ? HERO_IMAGES.memorials : 
                    HERO_IMAGES.default;

  // Count how many contact items we have for centering
  const contactItems = [settings.phone, settings.email, settings.address, settings.website].filter(Boolean);
  const contactGridClass = contactItems.length <= 2 
    ? 'flex flex-wrap justify-center gap-6' 
    : contactItems.length === 3 
      ? 'grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-4xl mx-auto'
      : 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6';

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Hero Section */}
      <header className="relative min-h-[90vh] flex items-center overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0">
          <img 
            src={heroImage}
            alt="Background"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-stone-900/95 via-stone-900/80 to-stone-900/40"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-stone-900/60 via-transparent to-transparent"></div>
        </div>

        {/* Decorative Elements */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
          <div className="absolute top-20 right-20 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 left-20 w-72 h-72 bg-stone-500/10 rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-20 w-full">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="space-y-8">
              {/* Logo */}
              {settings.logo_url && (
                <img 
                  src={settings.logo_url} 
                  alt={settings.company_name}
                  className="h-16 md:h-20 object-contain"
                  data-testid="company-logo"
                />
              )}

              {/* Company Name & Tagline */}
              <div className="space-y-4">
                <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl font-medium tracking-tight text-white leading-[1.1]" data-testid="company-name">
                  {settings.company_name}
                </h1>
                {settings.tagline && (
                  <p className="text-xl md:text-2xl text-stone-300 font-light max-w-xl leading-relaxed">
                    {settings.tagline}
                  </p>
                )}
              </div>

              {/* Hero Title & Subtitle */}
              <div className="space-y-3 pt-4">
                <h2 className="text-2xl md:text-3xl text-white font-medium">
                  {settings.hero_title}
                </h2>
                <p className="text-stone-400 text-lg max-w-lg">
                  {settings.hero_subtitle}
                </p>
              </div>

              {/* CTA Button */}
              <div className="flex flex-wrap gap-4 pt-4">
                <Button 
                  onClick={() => navigate(`/quote/${companySlug}`)}
                  className="bg-amber-500 hover:bg-amber-400 text-stone-900 font-semibold rounded-full px-8 py-6 text-lg shadow-lg shadow-amber-500/25 hover:shadow-amber-500/40 transition-all hover:scale-105 active:scale-95"
                  data-testid="hero-quote-btn"
                >
                  Get Your Free Quote
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                {settings.phone && (
                  <a href={`tel:${settings.phone}`}>
                    <Button 
                      variant="outline"
                      className="border-white/30 text-white hover:bg-white/10 rounded-full px-8 py-6 text-lg backdrop-blur-sm"
                    >
                      <Phone className="w-5 h-5 mr-2" />
                      Call Us
                    </Button>
                  </a>
                )}
              </div>

              {/* Trust Badges */}
              <div className="flex flex-wrap gap-6 pt-6">
                <div className="flex items-center gap-2 text-stone-400">
                  <Shield className="w-5 h-5 text-amber-500" />
                  <span className="text-sm">Quality Guaranteed</span>
                </div>
                <div className="flex items-center gap-2 text-stone-400">
                  <Award className="w-5 h-5 text-amber-500" />
                  <span className="text-sm">Expert Craftsmen</span>
                </div>
                <div className="flex items-center gap-2 text-stone-400">
                  <Sparkles className="w-5 h-5 text-amber-500" />
                  <span className="text-sm">Free Consultation</span>
                </div>
              </div>
            </div>

            {/* Right - Glass Card with Quote Options */}
            <div className="lg:justify-self-end w-full max-w-md">
              <div className="backdrop-blur-xl bg-white/10 border border-white/20 rounded-3xl p-8 shadow-2xl">
                <h3 className="text-xl font-semibold text-white mb-6 text-center">Choose Your Quote Type</h3>
                <div className="space-y-4">
                  {settings.enable_instant_quote && (
                    <button
                      onClick={() => navigate(`/quote/${companySlug}`)}
                      className="w-full group"
                      data-testid="instant-quote-card"
                    >
                      <div className="flex items-center gap-4 p-4 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-emerald-500/50 rounded-2xl transition-all duration-300">
                        <div className="w-14 h-14 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-xl flex items-center justify-center shadow-lg shadow-emerald-500/30 group-hover:scale-110 transition-transform">
                          <Zap className="w-7 h-7 text-white" />
                        </div>
                        <div className="flex-1 text-left">
                          <h4 className="text-white font-semibold text-lg">{settings.instant_quote_title}</h4>
                          <p className="text-stone-400 text-sm">{settings.instant_quote_description}</p>
                        </div>
                        <ArrowRight className="w-5 h-5 text-stone-500 group-hover:text-emerald-400 group-hover:translate-x-1 transition-all" />
                      </div>
                    </button>
                  )}

                  {settings.enable_24hour_quote && (
                    <button
                      onClick={() => navigate(`/quote/${companySlug}`)}
                      className="w-full group"
                      data-testid="24hour-quote-card"
                    >
                      <div className="flex items-center gap-4 p-4 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-blue-500/50 rounded-2xl transition-all duration-300">
                        <div className="w-14 h-14 bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/30 group-hover:scale-110 transition-transform">
                          <Clock className="w-7 h-7 text-white" />
                        </div>
                        <div className="flex-1 text-left">
                          <h4 className="text-white font-semibold text-lg">{settings.hour24_quote_title}</h4>
                          <p className="text-stone-400 text-sm">{settings.hour24_quote_description}</p>
                        </div>
                        <ArrowRight className="w-5 h-5 text-stone-500 group-hover:text-blue-400 group-hover:translate-x-1 transition-all" />
                      </div>
                    </button>
                  )}

                  {settings.enable_hearth_quote && (
                    <button
                      onClick={() => navigate(`/hearth-quote/${companySlug}`)}
                      className="w-full group"
                      data-testid="hearth-quote-card"
                    >
                      <div className="flex items-center gap-4 p-4 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-orange-500/50 rounded-2xl transition-all duration-300">
                        <div className="w-14 h-14 bg-gradient-to-br from-orange-400 to-orange-600 rounded-xl flex items-center justify-center shadow-lg shadow-orange-500/30 group-hover:scale-110 transition-transform">
                          <Flame className="w-7 h-7 text-white" />
                        </div>
                        <div className="flex-1 text-left">
                          <h4 className="text-white font-semibold text-lg">{settings.hearth_quote_title}</h4>
                          <p className="text-stone-400 text-sm">{settings.hearth_quote_description}</p>
                        </div>
                        <ArrowRight className="w-5 h-5 text-stone-500 group-hover:text-orange-400 group-hover:translate-x-1 transition-all" />
                      </div>
                    </button>
                  )}

                  {settings.enable_trade_account && (
                    <button
                      onClick={() => setShowTradeAccountDialog(true)}
                      className="w-full group"
                      data-testid="trade-account-card"
                    >
                      <div className="flex items-center gap-4 p-4 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-purple-500/50 rounded-2xl transition-all duration-300">
                        <div className="w-14 h-14 bg-gradient-to-br from-purple-400 to-purple-600 rounded-xl flex items-center justify-center shadow-lg shadow-purple-500/30 group-hover:scale-110 transition-transform">
                          <Briefcase className="w-7 h-7 text-white" />
                        </div>
                        <div className="flex-1 text-left">
                          <h4 className="text-white font-semibold text-lg">{settings.trade_account_title || 'Trade Account'}</h4>
                          <p className="text-stone-400 text-sm">{settings.trade_account_description || 'Apply for trade pricing'}</p>
                        </div>
                        <ArrowRight className="w-5 h-5 text-stone-500 group-hover:text-purple-400 group-hover:translate-x-1 transition-all" />
                      </div>
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex items-start justify-center p-2">
            <div className="w-1.5 h-3 bg-white/50 rounded-full animate-pulse"></div>
          </div>
        </div>
      </header>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-stone-100 rounded-full mb-6">
              <span className="w-2 h-2 bg-amber-500 rounded-full"></span>
              <span className="text-sm font-medium text-stone-600 tracking-wide uppercase">Why Choose Us</span>
            </div>
            <h2 className="font-serif text-4xl md:text-5xl font-medium text-stone-900 tracking-tight">
              Experience the Difference
            </h2>
            <p className="text-stone-500 text-lg mt-4 max-w-2xl mx-auto">
              We combine traditional craftsmanship with modern technology to deliver exceptional results.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-amber-100 rounded-2xl flex items-center justify-center mx-auto mb-5">
                <Users className="w-8 h-8 text-amber-600" />
              </div>
              <h3 className="font-semibold text-stone-900 text-lg mb-2">Personal Service</h3>
              <p className="text-stone-500 text-sm leading-relaxed">
                Dedicated support from consultation through to completion. We're with you every step of the way.
              </p>
            </div>

            <div className="text-center p-6">
              <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-5">
                <Ruler className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="font-semibold text-stone-900 text-lg mb-2">Precision Work</h3>
              <p className="text-stone-500 text-sm leading-relaxed">
                Accurate measurements and meticulous attention to detail ensure a perfect fit every time.
              </p>
            </div>

            <div className="text-center p-6">
              <div className="w-16 h-16 bg-emerald-100 rounded-2xl flex items-center justify-center mx-auto mb-5">
                <Palette className="w-8 h-8 text-emerald-600" />
              </div>
              <h3 className="font-semibold text-stone-900 text-lg mb-2">Quality Materials</h3>
              <p className="text-stone-500 text-sm leading-relaxed">
                We source only the finest materials from trusted suppliers to ensure lasting beauty.
              </p>
            </div>

            <div className="text-center p-6">
              <div className="w-16 h-16 bg-purple-100 rounded-2xl flex items-center justify-center mx-auto mb-5">
                <CheckCircle className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="font-semibold text-stone-900 text-lg mb-2">Satisfaction Guaranteed</h3>
              <p className="text-stone-500 text-sm leading-relaxed">
                Your complete satisfaction is our priority. We stand behind every project we undertake.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      {settings.description && (
        <section className="py-24 md:py-32 bg-stone-100 relative overflow-hidden">
          <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              {/* Image */}
              <div className="relative">
                <div className="aspect-[4/3] rounded-3xl overflow-hidden shadow-2xl">
                  <img 
                    src="https://images.pexels.com/photos/5502945/pexels-photo-5502945.jpeg?auto=compress&w=800"
                    alt="Craftsmanship"
                    className="w-full h-full object-cover"
                  />
                </div>
                {/* Floating Badge */}
                <div className="absolute -bottom-6 -right-6 bg-stone-900 text-white p-6 rounded-2xl shadow-xl">
                  <p className="text-4xl font-serif font-medium text-amber-400">25+</p>
                  <p className="text-sm text-stone-400">Years Experience</p>
                </div>
              </div>

              {/* Content */}
              <div className="space-y-6">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-full">
                  <span className="w-2 h-2 bg-amber-500 rounded-full"></span>
                  <span className="text-sm font-medium text-stone-600 tracking-wide uppercase">About Us</span>
                </div>
                <h2 className="font-serif text-4xl md:text-5xl font-medium text-stone-900 tracking-tight leading-tight">
                  Crafted with Passion, Built to Last
                </h2>
                <p className="text-stone-600 text-lg leading-relaxed whitespace-pre-line">
                  {settings.description}
                </p>
                <Button 
                  onClick={() => navigate(`/quote/${companySlug}`)}
                  className="bg-stone-900 hover:bg-stone-800 text-white rounded-full px-8 py-6 text-lg mt-4 transition-all hover:scale-105"
                >
                  Start Your Project
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Our Process Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-stone-100 rounded-full mb-6">
              <span className="w-2 h-2 bg-amber-500 rounded-full"></span>
              <span className="text-sm font-medium text-stone-600 tracking-wide uppercase">Our Process</span>
            </div>
            <h2 className="font-serif text-4xl md:text-5xl font-medium text-stone-900 tracking-tight">
              How It Works
            </h2>
            <p className="text-stone-500 text-lg mt-4 max-w-2xl mx-auto">
              A simple, straightforward process designed with you in mind.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="relative">
              <div className="text-center p-8 bg-stone-50 rounded-2xl border border-stone-200">
                <div className="w-12 h-12 bg-amber-500 text-white rounded-full flex items-center justify-center mx-auto mb-5 text-xl font-bold">
                  1
                </div>
                <h3 className="font-semibold text-stone-900 text-lg mb-3">Request a Quote</h3>
                <p className="text-stone-500 text-sm leading-relaxed">
                  Fill out our simple online form or give us a call. We'll get back to you within 24 hours.
                </p>
              </div>
              <div className="hidden md:block absolute top-1/2 -right-4 w-8 h-0.5 bg-stone-300"></div>
            </div>

            <div className="relative">
              <div className="text-center p-8 bg-stone-50 rounded-2xl border border-stone-200">
                <div className="w-12 h-12 bg-amber-500 text-white rounded-full flex items-center justify-center mx-auto mb-5 text-xl font-bold">
                  2
                </div>
                <h3 className="font-semibold text-stone-900 text-lg mb-3">Consultation</h3>
                <p className="text-stone-500 text-sm leading-relaxed">
                  We'll discuss your requirements, take measurements, and help you choose the perfect options.
                </p>
              </div>
              <div className="hidden md:block absolute top-1/2 -right-4 w-8 h-0.5 bg-stone-300"></div>
            </div>

            <div className="relative">
              <div className="text-center p-8 bg-stone-50 rounded-2xl border border-stone-200">
                <div className="w-12 h-12 bg-amber-500 text-white rounded-full flex items-center justify-center mx-auto mb-5 text-xl font-bold">
                  3
                </div>
                <h3 className="font-semibold text-stone-900 text-lg mb-3">Expert Installation</h3>
                <p className="text-stone-500 text-sm leading-relaxed">
                  Our skilled craftsmen complete the work to the highest standards, leaving you with a beautiful result.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-24 md:py-32 bg-stone-100">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-full mb-6 shadow-sm">
              <span className="w-2 h-2 bg-amber-500 rounded-full"></span>
              <span className="text-sm font-medium text-stone-600 tracking-wide uppercase">Contact</span>
            </div>
            <h2 className="font-serif text-4xl md:text-5xl font-medium text-stone-900 tracking-tight">
              Get In Touch
            </h2>
            <p className="text-stone-500 text-lg mt-4 max-w-2xl mx-auto">
              Ready to start your project? Contact us today for a free consultation.
            </p>
          </div>

          <div className={contactGridClass}>
            {settings.phone && (
              <a 
                href={`tel:${settings.phone}`} 
                className="group bg-white rounded-2xl p-6 shadow-sm hover:shadow-xl transition-all duration-300 border border-stone-200 hover:border-amber-500/50 w-full sm:w-auto sm:min-w-[280px]"
                data-testid="contact-phone"
              >
                <div className="w-14 h-14 bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl flex items-center justify-center mb-4 shadow-lg shadow-blue-500/20 group-hover:scale-110 transition-transform mx-auto">
                  <Phone className="w-7 h-7 text-white" />
                </div>
                <p className="text-stone-400 text-sm font-medium uppercase tracking-wide mb-1 text-center">Phone</p>
                <p className="text-stone-900 font-semibold text-xl text-center">{settings.phone}</p>
              </a>
            )}
            
            {settings.email && (
              <a 
                href={`mailto:${settings.email}`} 
                className="group bg-white rounded-2xl p-6 shadow-sm hover:shadow-xl transition-all duration-300 border border-stone-200 hover:border-amber-500/50 w-full sm:w-auto sm:min-w-[280px]"
                data-testid="contact-email"
              >
                <div className="w-14 h-14 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-xl flex items-center justify-center mb-4 shadow-lg shadow-emerald-500/20 group-hover:scale-110 transition-transform mx-auto">
                  <Mail className="w-7 h-7 text-white" />
                </div>
                <p className="text-stone-400 text-sm font-medium uppercase tracking-wide mb-1 text-center">Email</p>
                <p className="text-stone-900 font-semibold text-lg break-all text-center">{settings.email}</p>
              </a>
            )}
            
            {settings.address && (
              <div className="group bg-white rounded-2xl p-6 shadow-sm hover:shadow-xl transition-all duration-300 border border-stone-200 hover:border-amber-500/50 w-full sm:w-auto sm:min-w-[280px]" data-testid="contact-address">
                <div className="w-14 h-14 bg-gradient-to-br from-purple-400 to-purple-600 rounded-xl flex items-center justify-center mb-4 shadow-lg shadow-purple-500/20 group-hover:scale-110 transition-transform mx-auto">
                  <MapPin className="w-7 h-7 text-white" />
                </div>
                <p className="text-stone-400 text-sm font-medium uppercase tracking-wide mb-1 text-center">Address</p>
                <p className="text-stone-900 font-semibold whitespace-pre-line text-center">{settings.address}</p>
              </div>
            )}
            
            {settings.website && (
              <a 
                href={settings.website.startsWith('http') ? settings.website : `https://${settings.website}`} 
                target="_blank" 
                rel="noopener noreferrer"
                className="group bg-white rounded-2xl p-6 shadow-sm hover:shadow-xl transition-all duration-300 border border-stone-200 hover:border-amber-500/50 w-full sm:w-auto sm:min-w-[280px]"
                data-testid="contact-website"
              >
                <div className="w-14 h-14 bg-gradient-to-br from-cyan-400 to-cyan-600 rounded-xl flex items-center justify-center mb-4 shadow-lg shadow-cyan-500/20 group-hover:scale-110 transition-transform mx-auto">
                  <Globe className="w-7 h-7 text-white" />
                </div>
                <p className="text-stone-400 text-sm font-medium uppercase tracking-wide mb-1 text-center">Website</p>
                <p className="text-stone-900 font-semibold break-all text-center">{settings.website}</p>
              </a>
            )}
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-16 bg-stone-900">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-serif text-3xl md:text-4xl font-medium text-white mb-4">
            Ready to Get Started?
          </h2>
          <p className="text-stone-400 text-lg mb-8 max-w-2xl mx-auto">
            Get your free, no-obligation quote today. Our team is ready to help bring your vision to life.
          </p>
          <Button 
            onClick={() => navigate(`/quote/${companySlug}`)}
            className="bg-amber-500 hover:bg-amber-400 text-stone-900 font-semibold rounded-full px-10 py-6 text-lg shadow-lg shadow-amber-500/25 hover:shadow-amber-500/40 transition-all hover:scale-105"
          >
            Get Your Free Quote
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-stone-950 py-12">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              {settings.logo_url && (
                <img 
                  src={settings.logo_url} 
                  alt={settings.company_name}
                  className="h-10 object-contain brightness-0 invert opacity-80"
                />
              )}
              <span className="text-stone-400 text-lg font-serif">{settings.company_name}</span>
            </div>
            
            <div className="text-center md:text-right">
              <p className="text-stone-500 text-sm">
                © {new Date().getFullYear()} {settings.company_name}. All rights reserved.
              </p>
              {settings.footer_text && (
                <p className="text-stone-600 text-sm mt-1">{settings.footer_text}</p>
              )}
            </div>
          </div>
          
          {/* Powered By */}
          <div className="mt-8 pt-8 border-t border-stone-800 text-center">
            <p className="text-stone-600 text-xs">
              Powered by <span className="text-amber-500 font-medium">Memorial Desk</span>
            </p>
          </div>
        </div>
      </footer>

      {/* Trade Account Dialog */}
      {showTradeAccountDialog && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-2xl">
            {!tradeAccountSubmitted ? (
              <>
                <div className="p-6 border-b bg-gradient-to-r from-purple-50 to-violet-50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                        <Briefcase className="w-6 h-6 text-purple-600" />
                      </div>
                      <div>
                        <h2 className="text-xl font-bold text-gray-900">{settings.trade_account_title || 'Trade Account'}</h2>
                        <p className="text-sm text-gray-600">{settings.trade_account_description || 'Apply for exclusive trade pricing'}</p>
                      </div>
                    </div>
                    <button 
                      onClick={() => setShowTradeAccountDialog(false)}
                      className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center"
                    >
                      <span className="text-gray-500 text-lg">×</span>
                    </button>
                  </div>
                  
                  {settings.trade_account_benefits && (
                    <div className="mt-4 flex flex-wrap gap-2">
                      {settings.trade_account_benefits.split(',').map((benefit, idx) => (
                        <span key={idx} className="px-3 py-1 bg-purple-100 text-purple-700 text-sm rounded-full flex items-center gap-1">
                          <CheckCircle className="w-3 h-3" />
                          {benefit.trim()}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                
                <form onSubmit={handleTradeAccountSubmit} className="p-6 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Business Name *</label>
                    <input
                      type="text"
                      required
                      value={tradeAccountForm.business_name}
                      onChange={(e) => setTradeAccountForm(prev => ({ ...prev, business_name: e.target.value }))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="Your company name"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Contact Name *</label>
                    <input
                      type="text"
                      required
                      value={tradeAccountForm.contact_name}
                      onChange={(e) => setTradeAccountForm(prev => ({ ...prev, contact_name: e.target.value }))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="Your name"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                      <input
                        type="email"
                        required
                        value={tradeAccountForm.email}
                        onChange={(e) => setTradeAccountForm(prev => ({ ...prev, email: e.target.value }))}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        placeholder="email@company.com"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Phone *</label>
                      <input
                        type="tel"
                        required
                        value={tradeAccountForm.phone}
                        onChange={(e) => setTradeAccountForm(prev => ({ ...prev, phone: e.target.value }))}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        placeholder="01onal 567890"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Business Address</label>
                    <input
                      type="text"
                      value={tradeAccountForm.address}
                      onChange={(e) => setTradeAccountForm(prev => ({ ...prev, address: e.target.value }))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="Your business address"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Business Type</label>
                    <select
                      value={tradeAccountForm.business_type}
                      onChange={(e) => setTradeAccountForm(prev => ({ ...prev, business_type: e.target.value }))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      <option value="">Select your industry</option>
                      <option value="kitchen_fitter">Kitchen Fitter</option>
                      <option value="bathroom_specialist">Bathroom Specialist</option>
                      <option value="builder">Builder / Contractor</option>
                      <option value="interior_designer">Interior Designer</option>
                      <option value="property_developer">Property Developer</option>
                      <option value="kitchen_showroom">Kitchen Showroom</option>
                      <option value="fireplace_specialist">Fireplace Specialist</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">How did you hear about us?</label>
                    <input
                      type="text"
                      value={tradeAccountForm.how_heard}
                      onChange={(e) => setTradeAccountForm(prev => ({ ...prev, how_heard: e.target.value }))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="e.g., Referral, Google, Trade show"
                    />
                  </div>
                  
                  <div className="pt-4 flex gap-3">
                    <button
                      type="button"
                      onClick={() => setShowTradeAccountDialog(false)}
                      className="flex-1 px-4 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={submittingTradeAccount}
                      className="flex-1 px-4 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-semibold transition-colors disabled:opacity-50"
                    >
                      {submittingTradeAccount ? 'Submitting...' : 'Submit Application'}
                    </button>
                  </div>
                </form>
              </>
            ) : (
              <div className="p-8 text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Application Submitted!</h2>
                <p className="text-gray-600 mb-6">
                  Thank you for applying for a trade account. We'll review your application and get back to you within 24-48 hours.
                </p>
                <button
                  onClick={() => {
                    setShowTradeAccountDialog(false);
                    setTradeAccountSubmitted(false);
                    setTradeAccountForm({
                      business_name: '',
                      contact_name: '',
                      email: '',
                      phone: '',
                      address: '',
                      business_type: '',
                      how_heard: ''
                    });
                  }}
                  className="px-6 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-700 transition-colors"
                >
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default CompanyLandingPage;
